import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../auth/AuthProvider';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useContext(AuthContext);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add effect to update cart count from localStorage
  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const count = cart.reduce((total, item) => total + (item.quantity || 1), 0);
      setCartCount(count);
    };

    // Update cart count on mount
    updateCartCount();

    // Listen for storage events to update cart count when cart changes
    const handleStorageChange = (e) => {
      if (e.key === 'cart') {
        updateCartCount();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Custom event for when cart is updated within the same window
    const handleCartUpdate = () => updateCartCount();
    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${'bg-gradient-to-b from-black via-black to-transparent pt-8 pb-10 md:pb-20'
      }`} style={{
        backgroundSize: '100% 100%',
        backgroundPosition: 'top',
        backdropFilter: 'blur(0px)',
        background: 'linear-gradient(to bottom, black 10%, black, transparent)'
      }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 min-w-full">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img src="/images/logo.svg" alt="Logo" className="max-h-[42px] w-auto hidden lg:block" />
            <img src="/images/logo-small.svg" alt="Logo" className="max-h-[42px] w-auto block lg:hidden" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 text-[16px]">
            <Link to="/gallery" className="text-white hover:text-accent transition-colors">
              All maps
            </Link>
            <Link to="/gift-card" className="text-white hover:text-accent transition-colors">
              Gift card
            </Link>

            <Link to="/about" className="text-white hover:text-accent transition-colors">
              About us
            </Link>

            {isAuthenticated ? (
              <div className="relative">
                <a
                  onClick={toggleUserMenu}
                  className="flex items-center text-white hover:text-accent transition-colors focus:outline-none cursor-pointer"
                >
                  <span className="mr-2">{user.username}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </a>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <a
                  className="text-white hover:text-accent transition-colors cursor-pointer"
                  onClick={() => document.dispatchEvent(new CustomEvent('open-login-modal'))}
                >
                  Log In
                </a>
              </div>

            )}
            <Link to="/cart" className="relative p-1.5 rounded-full">
              <span className="text-md !text-white hover:text-accent transition-colors">Cart</span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex justify-center items-center text-[10px]">
                  {cartCount}
                </span>
              )}
            </Link>

            <Link to="/create" className="text-white hover:bg-white hover:text-accent transition-colors border border-white rounded-full px-8 py-2">
              Create Your Star Map
            </Link>

          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden text-white hover:bg-accent/10 p-2 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`px-4 bg-black md:hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100 py-4' : 'max-h-0 opacity-0 invisible'}`}>
          <div className="flex flex-col gap-4">
            <Link
              to="/create"
              className="text-white py-2 rounded-lg hover:bg-accent/10 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Create Your Map
            </Link>
            <Link
              to="/gallery"
              className="text-white py-2 rounded-lg hover:bg-accent/10 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Gallery
            </Link>
            <Link
              to="/about"
              className="text-white py-2 rounded-lg hover:bg-accent/10 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </Link>
            <Link
              to="/cart"
              className="text-white py-2 rounded-lg hover:bg-accent/10 transition-colors flex items-center"
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="mr-2">Cart</span>
              <span className="text-lg">🛒</span>
              {cartCount > 0 && (
                <span className="ml-2 bg-red-600 text-white rounded-full w-4 h-4 flex justify-center items-center text-[10px]">
                  {cartCount}
                </span>
              )}
            </Link>

            {isAuthenticated ? (
              <>
                <div className="border-t border-white/20 my-2"></div>
                <Link
                  to="/profile"
                  className="text-white py-2 rounded-lg hover:bg-accent/10 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Profile
                </Link>
                <button
                  onClick={() => { logout(); setIsMenuOpen(false); }}
                  className="text-red-300 py-2 rounded-lg hover:bg-accent/10 transition-colors text-left"
                >
                  Log Out
                </button>
              </>
            ) : (
              <>
                <button
                  className="text-white py-2 rounded-lg hover:bg-accent/10 transition-colors text-left w-full"
                  onClick={() => {
                    setIsMenuOpen(false);
                    document.dispatchEvent(new CustomEvent('open-login-modal'));
                  }}
                >
                  Log In
                </button>
                <button
                  className="bg-accent text-primary py-2 rounded-lg font-medium text-center w-full mt-4"
                  onClick={() => {
                    setIsMenuOpen(false);
                    document.dispatchEvent(new CustomEvent('open-register-modal'));
                  }}
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 